import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Share2, Copy, Music4, Film, Pencil, X, Save, Shuffle } from "lucide-react";
import {
  getMovie, shareUrl, updateNarration, updatePrompt, updateScene, rerollScene,
} from "@/lib/api";
import MoviePlayer from "@/components/MoviePlayer";
import GenerationProgress from "@/components/GenerationProgress";

export default function MovieDetail() {
  const { id } = useParams();
  const [movie, setMovie] = useState(null);
  const [audioBust, setAudioBust] = useState(0);
  const [videoBust, setVideoBust] = useState(0);

  // narration editor
  const [editingNar, setEditingNar] = useState(false);
  const [draftNar, setDraftNar] = useState("");
  const [savingNar, setSavingNar] = useState(false);
  const narRef = useRef(null);

  // prompt editor
  const [editingPrompt, setEditingPrompt] = useState(false);
  const [draftPrompt, setDraftPrompt] = useState("");
  const [savingPrompt, setSavingPrompt] = useState(false);

  // scene editor
  const [editingSceneIndex, setEditingSceneIndex] = useState(null);
  const [draftScene, setDraftScene] = useState({ description: "", beat: "" });
  const [savingScene, setSavingScene] = useState(false);
  const [rerollingIndex, setRerollingIndex] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let handle = null;
    const tick = async () => {
      try {
        const m = await getMovie(id);
        if (cancelled) return;
        setMovie(m);
        if (m.status !== "complete" && m.status !== "failed") {
          handle = setTimeout(tick, 3000);
        }
      } catch {
        toast.error("Failed to load movie");
      }
    };
    tick();
    return () => {
      cancelled = true;
      if (handle) clearTimeout(handle);
    };
  }, [id]);

  useEffect(() => {
    if (editingNar && narRef.current) {
      narRef.current.focus();
      narRef.current.setSelectionRange(draftNar.length, draftNar.length);
    }
  }, [editingNar, draftNar.length]);

  const copyShare = async () => {
    const url = shareUrl(id);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied");
    } catch {
      window.prompt("Copy this share URL:", url);
    }
  };

  const startEditNar = () => { setDraftNar(movie?.narration || ""); setEditingNar(true); };
  const cancelEditNar = () => { setEditingNar(false); setDraftNar(""); };
  const saveNarration = async () => {
    if (!draftNar.trim()) { toast.error("Narration cannot be empty"); return; }
    setSavingNar(true);
    try {
      const updated = await updateNarration(id, { narration: draftNar.trim() });
      setMovie(updated);
      setEditingNar(false); setDraftNar("");
      setAudioBust(Date.now());
      toast.success("Voiceover re-recorded with your edits");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Re-record failed");
    } finally {
      setSavingNar(false);
    }
  };

  const startEditPrompt = () => { setDraftPrompt(movie?.prompt || ""); setEditingPrompt(true); };
  const cancelEditPrompt = () => { setEditingPrompt(false); setDraftPrompt(""); };
  const savePrompt = async () => {
    if (!draftPrompt.trim()) { toast.error("Prompt cannot be empty"); return; }
    setSavingPrompt(true);
    try {
      const updated = await updatePrompt(id, { prompt: draftPrompt.trim() });
      setMovie(updated);
      setEditingPrompt(false); setDraftPrompt("");
      toast("Re-rolling the entire movie...", { description: "Watch the pipeline pick it back up above." });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rewrite failed");
    } finally {
      setSavingPrompt(false);
    }
  };

  const startEditScene = (sc) => {
    setEditingSceneIndex(sc.index);
    setDraftScene({ description: sc.description || "", beat: sc.beat || "" });
  };
  const cancelEditScene = () => { setEditingSceneIndex(null); setDraftScene({ description: "", beat: "" }); };
  const saveScene = async () => {
    if (!draftScene.description.trim()) { toast.error("Description cannot be empty"); return; }
    setSavingScene(true);
    try {
      const updated = await updateScene(id, editingSceneIndex, {
        description: draftScene.description.trim(),
        beat: draftScene.beat.trim() || undefined,
      });
      setMovie(updated);
      setVideoBust(Date.now());
      cancelEditScene();
      toast.success(`Scene ${editingSceneIndex + 1} re-rendered`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Re-render failed");
    } finally {
      setSavingScene(false);
    }
  };

  const reroll = async (idx) => {
    setRerollingIndex(idx);
    try {
      const updated = await rerollScene(id, idx);
      setMovie(updated);
      setVideoBust(Date.now());
      toast.success(`Scene ${idx + 1} re-rolled with a fresh seed`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Re-roll failed");
    } finally {
      setRerollingIndex(null);
    }
  };

  if (!movie) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-white/50" />
      </div>
    );
  }

  const ready = movie.status === "complete" && movie.has_video;
  const totalSeconds = movie.runtime || (movie.num_scenes || 1) * movie.duration;
  const playerMovie = { ...movie, _audioBust: audioBust, _videoBust: videoBust };

  return (
    <div className="mx-auto max-w-[1400px] px-6 py-12 md:px-10 md:py-16">
      <div className="mb-10 flex items-center justify-between">
        <Link to="/gallery" data-testid="back-to-gallery"
              className="inline-flex items-center gap-2 font-mono-ui text-[11px] uppercase tracking-[0.28em] text-white/50 hover:text-white">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back to Reels
        </Link>
        {ready && (
          <div className="flex items-center gap-2">
            <button
              data-testid="edit-narration-top"
              onClick={startEditNar}
              className="inline-flex items-center gap-2 border border-[#E50914] bg-[#E50914] px-4 py-2 font-mono-ui text-[10px] uppercase tracking-[0.28em] text-white transition-colors duration-200 hover:bg-transparent"
            >
              <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} /> Edit Narration
            </button>
            <button
              data-testid="share-button"
              onClick={copyShare}
              className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 font-mono-ui text-[10px] uppercase tracking-[0.28em] text-white/80 hover:border-[#E50914] hover:text-white"
            >
              <Share2 className="h-3.5 w-3.5" strokeWidth={1.5} /> Copy Share Link
              <Copy className="h-3 w-3" strokeWidth={1.5} />
            </button>
          </div>
        )}
      </div>

      <header className="mb-10 border-b border-white/10 pb-8">
        <div className="flex items-center gap-3">
          <span className="font-mono-ui text-[10px] uppercase tracking-[0.32em] text-[#E50914]">{movie.genre}</span>
          <span className="h-px w-8 bg-white/20" />
          <span className="font-mono-ui text-[10px] uppercase tracking-[0.28em] text-white/40">
            {movie.num_scenes || 1} scene{(movie.num_scenes || 1) > 1 ? "s" : ""} · {totalSeconds}s · {movie.size}
          </span>
          {movie.music_mood && (
            <>
              <span className="h-px w-8 bg-white/20" />
              <span className="inline-flex items-center gap-1 font-mono-ui text-[10px] uppercase tracking-[0.28em] text-white/60">
                <Music4 className="h-3 w-3" strokeWidth={1.5} /> {movie.music_mood}
              </span>
            </>
          )}
        </div>
        <h1 data-testid="movie-title" className="mt-4 font-display text-5xl font-black leading-none tracking-tight md:text-7xl">
          {movie.title || "Untitled Reel"}
        </h1>
        {movie.logline && (
          <p className="mt-5 max-w-3xl font-display italic text-lg text-white/70 md:text-xl">
            &ldquo;{movie.logline}&rdquo;
          </p>
        )}
      </header>

      {ready ? (
        <div className="space-y-10">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <MoviePlayer key={`${audioBust}-${videoBust}`} movie={playerMovie} />
            </div>

            {/* Original prompt editor */}
            <aside className="lg:col-span-4">
              <div className="border border-white/10 bg-[#0A0A0A] p-6">
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Film className="h-4 w-4 text-[#E50914]" strokeWidth={1.5} />
                    <div className="font-mono-ui text-[10px] uppercase tracking-[0.32em] text-white/60">
                      Original Prompt
                    </div>
                  </div>
                  {!editingPrompt ? (
                    <button
                      data-testid="edit-prompt"
                      onClick={startEditPrompt}
                      className="inline-flex items-center gap-1.5 border border-white/15 px-2.5 py-1 font-mono-ui text-[9px] uppercase tracking-[0.28em] text-white/70 hover:border-[#E50914] hover:text-white"
                    >
                      <Pencil className="h-3 w-3" strokeWidth={1.5} /> Rewrite
                    </button>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <button
                        data-testid="cancel-prompt"
                        onClick={cancelEditPrompt}
                        disabled={savingPrompt}
                        className="inline-flex items-center gap-1 border border-white/15 px-2 py-1 font-mono-ui text-[9px] uppercase tracking-[0.28em] text-white/70 hover:border-white/50 hover:text-white disabled:opacity-40"
                      >
                        <X className="h-3 w-3" strokeWidth={1.5} />
                      </button>
                      <button
                        data-testid="save-prompt"
                        onClick={savePrompt}
                        disabled={savingPrompt || !draftPrompt.trim()}
                        className="inline-flex items-center gap-1 border border-[#E50914] bg-[#E50914] px-2.5 py-1 font-mono-ui text-[9px] uppercase tracking-[0.28em] text-white hover:bg-transparent disabled:opacity-40"
                      >
                        {savingPrompt ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" strokeWidth={1.5} />}
                        Re-roll
                      </button>
                    </div>
                  )}
                </div>

                {editingPrompt ? (
                  <>
                    <textarea
                      data-testid="prompt-textarea"
                      value={draftPrompt}
                      onChange={(e) => setDraftPrompt(e.target.value)}
                      disabled={savingPrompt}
                      rows={5}
                      className="w-full resize-y border border-white/10 bg-black px-3 py-2 font-mono-ui text-sm leading-relaxed text-white outline-none focus:border-[#E50914] disabled:opacity-60"
                    />
                    <p className="mt-2 font-mono-ui text-[9px] uppercase tracking-[0.26em] text-white/40">
                      Saving will rewrite the script AND re-render every scene.
                    </p>
                  </>
                ) : (
                  <p className="text-sm leading-relaxed text-white/70">{movie.prompt}</p>
                )}
              </div>
            </aside>
          </div>

          {/* NARRATOR TRANSCRIPT — editable */}
          <div data-testid="narration-panel" className="border border-white/10 bg-[#0A0A0A] p-8 md:p-10">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="inline-block h-2 w-2 bg-[#E50914]" />
                <div className="font-mono-ui text-[10px] uppercase tracking-[0.32em] text-white/60">
                  Narrator Transcript
                </div>
                {editingNar && (
                  <span className="border border-[#E50914]/60 bg-[#E50914]/10 px-2 py-1 font-mono-ui text-[9px] uppercase tracking-[0.28em] text-white">
                    Editing
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {!editingNar ? (
                  <button
                    data-testid="edit-narration-inline"
                    onClick={startEditNar}
                    className="inline-flex items-center gap-2 border border-white/15 px-3 py-1.5 font-mono-ui text-[10px] uppercase tracking-[0.28em] text-white/80 hover:border-[#E50914] hover:text-white"
                  >
                    <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} /> Edit &amp; re-record
                  </button>
                ) : (
                  <>
                    <button
                      data-testid="cancel-narration"
                      onClick={cancelEditNar}
                      disabled={savingNar}
                      className="inline-flex items-center gap-2 border border-white/15 px-3 py-1.5 font-mono-ui text-[10px] uppercase tracking-[0.28em] text-white/70 hover:border-white/50 hover:text-white disabled:opacity-40"
                    >
                      <X className="h-3.5 w-3.5" strokeWidth={1.5} /> Cancel
                    </button>
                    <button
                      data-testid="save-narration"
                      onClick={saveNarration}
                      disabled={savingNar || !draftNar.trim()}
                      className="inline-flex items-center gap-2 border border-[#E50914] bg-[#E50914] px-4 py-1.5 font-mono-ui text-[10px] uppercase tracking-[0.28em] text-white transition-colors duration-200 hover:bg-transparent disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {savingNar ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} /> : <Save className="h-3.5 w-3.5" strokeWidth={1.5} />}
                      Save &amp; re-record
                    </button>
                  </>
                )}
              </div>
            </div>

            {editingNar ? (
              <>
                <textarea
                  ref={narRef}
                  data-testid="narration-textarea"
                  value={draftNar}
                  onChange={(e) => setDraftNar(e.target.value)}
                  disabled={savingNar}
                  rows={Math.max(6, Math.min(20, Math.ceil(draftNar.length / 90) + 3))}
                  className="w-full resize-y border border-white/10 bg-black px-4 py-3 font-mono-ui text-base leading-loose text-white outline-none focus:border-[#E50914] disabled:opacity-60"
                  placeholder="Type exactly what the narrator should say…"
                />
                <div className="mt-2 flex items-center justify-between font-mono-ui text-[10px] uppercase tracking-[0.28em] text-white/40">
                  <span>{draftNar.trim().split(/\s+/).filter(Boolean).length} words · ~{Math.max(1, Math.round(draftNar.trim().split(/\s+/).filter(Boolean).length / 2.4))}s spoken</span>
                  <span>Voice: {movie.voice} · edge-tts neural</span>
                </div>
              </>
            ) : (
              <p data-testid="movie-narration" className="whitespace-pre-wrap font-mono-ui text-base leading-loose text-white/85">
                {movie.narration || "—"}
              </p>
            )}
          </div>

          {/* SHOT LIST — every scene individually editable */}
          {movie.scenes?.length > 0 && (
            <div className="border border-white/10 bg-[#0A0A0A] p-6 md:p-8">
              <div className="mb-6 flex items-center gap-3">
                <Film className="h-4 w-4 text-[#E50914]" strokeWidth={1.5} />
                <div className="font-mono-ui text-[10px] uppercase tracking-[0.32em] text-white/60">
                  Shot List · {movie.scenes.length} scenes · Click any card to edit or re-roll
                </div>
              </div>

              <div className="grid grid-cols-1 gap-px bg-white/5 md:grid-cols-2 lg:grid-cols-3">
                {movie.scenes.map((sc) => {
                  const isEditing = editingSceneIndex === sc.index;
                  const isRerolling = rerollingIndex === sc.index;
                  return (
                    <div key={sc.index} data-testid={`shot-${sc.index}`} className="bg-[#0A0A0A] p-5">
                      <div className="mb-2 flex items-center justify-between">
                        <div className="font-mono-ui text-[10px] uppercase tracking-[0.28em] text-[#E50914]">
                          Scene {String(sc.index + 1).padStart(2, "0")}
                        </div>
                        {!isEditing && (
                          <div className="flex items-center gap-1">
                            <button
                              data-testid={`reroll-scene-${sc.index}`}
                              onClick={() => reroll(sc.index)}
                              disabled={isRerolling || savingScene}
                              title="Re-roll this scene with a fresh seed"
                              className="inline-flex items-center gap-1 border border-white/10 px-2 py-1 font-mono-ui text-[9px] uppercase tracking-[0.24em] text-white/70 hover:border-[#E50914] hover:text-white disabled:opacity-40"
                            >
                              {isRerolling ? <Loader2 className="h-3 w-3 animate-spin" /> : <Shuffle className="h-3 w-3" strokeWidth={1.5} />}
                              Reroll
                            </button>
                            <button
                              data-testid={`edit-scene-${sc.index}`}
                              onClick={() => startEditScene(sc)}
                              className="inline-flex items-center gap-1 border border-white/10 px-2 py-1 font-mono-ui text-[9px] uppercase tracking-[0.24em] text-white/70 hover:border-[#E50914] hover:text-white"
                            >
                              <Pencil className="h-3 w-3" strokeWidth={1.5} /> Edit
                            </button>
                          </div>
                        )}
                      </div>

                      {isEditing ? (
                        <>
                          <input
                            data-testid={`scene-beat-input-${sc.index}`}
                            value={draftScene.beat}
                            onChange={(e) => setDraftScene((d) => ({ ...d, beat: e.target.value }))}
                            disabled={savingScene}
                            className="mb-2 w-full border border-white/10 bg-black px-3 py-2 font-display text-lg font-bold tracking-tight text-white outline-none focus:border-[#E50914] disabled:opacity-60"
                            placeholder="Scene beat"
                          />
                          <textarea
                            data-testid={`scene-desc-input-${sc.index}`}
                            value={draftScene.description}
                            onChange={(e) => setDraftScene((d) => ({ ...d, description: e.target.value }))}
                            disabled={savingScene}
                            rows={6}
                            className="w-full resize-y border border-white/10 bg-black px-3 py-2 font-mono-ui text-xs leading-relaxed text-white outline-none focus:border-[#E50914] disabled:opacity-60"
                            placeholder="Scene description sent to Flux — camera, subject, lighting, mood."
                          />
                          <div className="mt-2 flex items-center justify-end gap-2">
                            <button
                              data-testid={`cancel-scene-${sc.index}`}
                              onClick={cancelEditScene}
                              disabled={savingScene}
                              className="inline-flex items-center gap-1 border border-white/15 px-2.5 py-1 font-mono-ui text-[9px] uppercase tracking-[0.28em] text-white/70 hover:border-white/50 hover:text-white disabled:opacity-40"
                            >
                              <X className="h-3 w-3" strokeWidth={1.5} /> Cancel
                            </button>
                            <button
                              data-testid={`save-scene-${sc.index}`}
                              onClick={saveScene}
                              disabled={savingScene || !draftScene.description.trim()}
                              className="inline-flex items-center gap-1 border border-[#E50914] bg-[#E50914] px-3 py-1 font-mono-ui text-[9px] uppercase tracking-[0.28em] text-white hover:bg-transparent disabled:opacity-40"
                            >
                              {savingScene ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" strokeWidth={1.5} />}
                              Save &amp; re-render
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="mt-2 font-display text-base font-bold tracking-tight">
                            {sc.beat || `Scene ${sc.index + 1}`}
                          </div>
                          <p className="mt-2 line-clamp-4 text-sm leading-relaxed text-white/60">
                            {sc.description}
                          </p>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ) : (
        <GenerationProgress movie={movie} />
      )}
    </div>
  );
}
